import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FetchUsersOrderByOptions, OPERATING_SYSTEM } from 'enums';
import {
  createSurvey,
  getActivitiesByUserIdOrStripeIdOrActivityType,
  getSurveys,
  getTasksByUserId,
  getUserByIdOrStripeIdOrEmail,
  getUserDevices,
  getUserInfoAndFocusModes,
  getUsers,
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from './extra';
import {
  FIRST_DEVICE_INFO_INDEX,
  NO_ELEMENT_FOUND,
  PAGINATE_ITEMS
} from 'constants/general';
import { initialAdminState } from 'store/initial-states';
import { increment } from 'utils/helpers';
import { DEFAULT_PLATFORM_DEVICES } from 'assets/data';
import { DBUser } from 'interfaces/common.interface';
import { Announcement } from 'interfaces/admin.interface';
import { DeviceInfo, SearchByPayload, SurveyType } from 'interfaces';

const admin = createSlice({
  name: 'admin',
  initialState: initialAdminState,
  reducers: {
    getNextPage: (state) => {
      state.skip = state.skip + PAGINATE_ITEMS;
    },
    getPrevPage: (state) => {
      state.skip = state.skip - PAGINATE_ITEMS;
    },
    clearFocusModesAndActivities: (state) => {
      state.focusModes = [];
      state.userActivities = [];
    },
    updateOrderBy: (
      state,
      { payload }: PayloadAction<FetchUsersOrderByOptions>
    ) => {
      state.orderBy = payload;
    },
    updateIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    resetPagination: (state) => {
      state.skip = 0;
    },
    updateIsFetchingActivities: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingActivities = payload;
    },
    updateIsFetchingFocusModes: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingFocusModes = payload;
    },
    updateUserActivitiesFilter: (state, { payload }: PayloadAction<string>) => {
      state.userActivitiesFilter = payload;
    },
    updateActivityOrFocusModeSearchResult: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.activityOrFocusModeSearchResult = payload;
    },
    updateShowRequestDetailsModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showRequestDetailsModal = payload;
    },
    updateAccessRequestedUserId: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.accessRequestedUserId = payload;
    },
    updateIsAccessRequesting: (state, { payload }: PayloadAction<boolean>) => {
      state.isAccessRequesting = payload;
    },
    updateIsAccessRequestGranted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAccessRequestGranted = payload;
    },
    updateSearchBy: (state, { payload }: PayloadAction<SearchByPayload>) => {
      state.searchBy = payload;
    },
    updateIsSearchByValid: (
      state,
      {
        payload
      }: PayloadAction<{
        user_id: boolean;
        stripe_customer_id: boolean;
        email: boolean;
      }>
    ) => {
      state.isSearchByValid = payload;
    },
    updateIsFetchingUserDetails: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingUserDetails = payload;
    },
    updateIsDeviceFetching: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeviceFetching = payload;
    },
    updateIsCreatingSurvey: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingSurvey = payload;
    },
    updateIsUpdatingSurvey: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingSurvey = payload;
    },
    updateIsExtendingTrial: (state, { payload }: PayloadAction<boolean>) => {
      // flag to indicate extend trial API in progress
      // added for subscription management UI
      state.isExtendingTrial = payload;
    },
    updateIsCancelingSubscription: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      // flag to indicate cancel subscription API in progress
      state.isCancelingSubscription = payload;
    },
    updateIsUpgradingPlan: (state, { payload }: PayloadAction<boolean>) => {
      // flag to indicate upgrade plan API in progress
      state.isUpgradingPlan = payload;
    },
    updateIsFetchingSurveys: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingSurveys = payload;
    },
    updateIsFetchingTasks: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingTasks = payload;
    },
    updateShowSurveyDetailModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showSurveyDetailModal = payload;
    },
    updateSelectedSurvey: (
      state,
      { payload }: PayloadAction<SurveyType | null>
    ) => {
      state.selectedSurvey = payload;
    },
    updateIsFetchingAnnouncements: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingAnnouncements = payload;
    },
    updateIsCreatingAnnouncement: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCreatingAnnouncement = payload;
    },
    updateIsUpdatingAnnouncement: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingAnnouncement = payload;
    },
    updateIsDeletingAnnouncement: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isDeletingAnnouncement = payload;
    },
    updateSelectedAnnouncement: (
      state,
      { payload }: PayloadAction<Announcement | null>
    ) => {
      state.selectedAnnouncement = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.fulfilled, (state, { payload }) => {
        const defaultPlatformDevices =
          payload?.data?.map((user: DBUser) => ({
            ...user,
            devices: DEFAULT_PLATFORM_DEVICES
          })) ?? [];
        if (payload?.shouldAppend) {
          state.users.push(...defaultPlatformDevices);
        } else {
          state.users = defaultPlatformDevices;
        }
        state.isLoading = false;
      })
      .addCase(getUserInfoAndFocusModes.fulfilled, (state, { payload }) => {
        state.focusModes = payload?.focus_modes ?? [];
        state.isFetchingFocusModes = false;
      })
      .addCase(
        getActivitiesByUserIdOrStripeIdOrActivityType.fulfilled,
        (state, { payload }) => {
          state.userActivities = payload;
          state.isFetchingActivities = false;
        }
      )
      .addCase(getUserByIdOrStripeIdOrEmail.fulfilled, (state, { payload }) => {
        state.users = payload ?? [];
        state.isFetchingUserDetails = false;
      })
      .addCase(getUserDevices.fulfilled, (state, { payload }) => {
        const osMap = {
          [OPERATING_SYSTEM.ANDROID]: 'android',
          [OPERATING_SYSTEM.IOS]: 'iOS',
          [OPERATING_SYSTEM.MAC]: 'macOS',
          [OPERATING_SYSTEM.WIN]: 'windows'
        };
        const index =
          payload?.length &&
          state.users.findIndex(
            (user) => user.id === payload?.[FIRST_DEVICE_INFO_INDEX]?.user_id
          );
        if (index !== NO_ELEMENT_FOUND) {
          const devices = { ...state.users[index].devices };
          payload?.forEach((device_info: DeviceInfo) => {
            const os = device_info.operating_system as keyof typeof osMap;
            const osKey = osMap[os] as keyof typeof devices;
            if (osKey) {
              devices[osKey] = increment(devices[osKey]);
            }
          });
          state.users[index].devices = devices;
        }
        state.isDeviceFetching = false;
      })
      .addCase(getTasksByUserId.fulfilled, (state, { payload }) => {
        state.userTasks = payload ?? [];
        state.isFetchingTasks = false;
      })
      .addCase(createSurvey.fulfilled, (state) => {
        state.isCreatingSurvey = false;
      })
      .addCase(getSurveys.fulfilled, (state, { payload }) => {
        state.surveys.push(...payload);
        state.isFetchingSurveys = false;
      })
      .addCase(getAnnouncements.fulfilled, (state, { payload }) => {
        state.announcements = payload?.data ?? [];
        state.announcementsMeta = payload?.meta ?? null;
        state.isFetchingAnnouncements = false;
      })
      .addCase(createAnnouncement.fulfilled, (state) => {
        state.isCreatingAnnouncement = false;
      })
      .addCase(updateAnnouncement.fulfilled, (state) => {
        state.isUpdatingAnnouncement = false;
      })
      .addCase(deleteAnnouncement.fulfilled, (state) => {
        state.isDeletingAnnouncement = false;
      });
  }
});

export const {
  getNextPage,
  getPrevPage,
  clearFocusModesAndActivities,
  updateOrderBy,
  updateIsLoading,
  resetPagination,
  updateIsFetchingActivities,
  updateIsFetchingFocusModes,
  updateUserActivitiesFilter,
  updateActivityOrFocusModeSearchResult,
  updateShowRequestDetailsModal,
  updateAccessRequestedUserId,
  updateIsAccessRequesting,
  updateIsAccessRequestGranted,
  updateIsFetchingUserDetails,
  updateIsSearchByValid,
  updateSearchBy,
  updateIsDeviceFetching,
  updateIsCreatingSurvey,
  updateIsExtendingTrial,
  updateIsCancelingSubscription,
  updateIsUpgradingPlan,
  updateIsFetchingSurveys,
  updateIsFetchingTasks,
  updateIsUpdatingSurvey,
  updateShowSurveyDetailModal,
  updateSelectedSurvey,
  updateIsFetchingAnnouncements,
  updateIsCreatingAnnouncement,
  updateIsUpdatingAnnouncement,
  updateIsDeletingAnnouncement,
  updateSelectedAnnouncement
} = admin.actions;

export default admin.reducer;
